/**
 * JobDetailView - Full page wrapper for UnifiedJobDetail
 * Complete Scheduling Workflow with Message Templates
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WorkspaceJobPanel from '../workspace/WorkspaceJobPanel';
import { MapPin, MessageSquare, Loader2, X, Calendar, Clock, Navigation, Play, Pause, CheckCircle, XCircle, Bell, AlertTriangle, Send, Edit3, FileText, FileCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import StandardBackButton from '@/components/StandardBackButton';
import FloatingActionMenu from '@/components/FloatingActionMenu';
import UnifiedJobDetail from '@/components/UnifiedJobDetail';
import LeadIdentityBadge from '@/components/LeadIdentityBadge';
import MyosFormFillModal from '@/components/MyosFormFillModal';
import { resolveLeadIdentities } from '@/lib/leadIdentity';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ MESSAGE TEMPLATES ============
const MESSAGE_TEMPLATES = {
  onMyWay: [
    { id: 'omw1', text: "Hi! I'm on my way and will arrive in approximately 20-30 minutes." },
    { id: 'omw2', text: "Heading your way now! I should be there shortly." },
    { id: 'omw3', text: "Just left and I'm on my way to you. See you soon!" },
    { id: 'omw4', text: "On my way! Please make sure the area is accessible." },
    { id: 'custom', text: 'Write custom message...' }
  ],
  cancelOnMyWay: [
    { id: 'comw1', text: "Sorry, I accidentally sent the 'On My Way' notification. I'm not actually en route yet. I'll notify you when I'm truly on my way." },
    { id: 'comw2', text: "Apologies for the confusion - that was sent by mistake. Please disregard the previous message." },
    { id: 'custom', text: 'Write custom message...' }
  ],
  start: [
    { id: 'st1', text: "I've arrived and I'm starting your service now." },
    { id: 'st2', text: "I'm here! Beginning work on your service." },
    { id: 'st3', text: "Just arrived at your location. Starting the service now." },
    { id: 'custom', text: 'Write custom message...' }
  ],
  pause: [
    { id: 'ps1', text: "Your service has been paused. We'll be in touch soon to schedule the next steps.", reason: 'general' },
    { id: 'ps2', text: "Service paused - waiting on authorization. We'll resume once approved.", reason: 'waiting_on_authorization' },
    { id: 'ps3', text: "Service paused - waiting on parts/materials. We'll contact you once everything arrives.", reason: 'waiting_on_parts' },
    { id: 'ps4', text: "Initial service provided. We'll follow up to complete the remaining work.", reason: 'service_provided' },
    { id: 'custom', text: 'Write custom message...', reason: 'custom' }
  ],
  complete: [
    { id: 'cp1', text: "Your service has been completed. Thank you for your business!" },
    { id: 'cp2', text: "Service complete! Please come pick up your belongings at your convenience." },
    { id: 'cp3', text: "All done! Your service has been successfully completed. Thank you!" },
    { id: 'cp4', text: "Service completed. Please pick up your items whenever you're ready." },
    { id: 'cp5', text: "Finished! Thanks for choosing us. We hope to see you again!" },
    { id: 'custom', text: 'Write custom message...' }
  ],
  reschedule: [
    { id: 'rs1', text: "Your appointment has been rescheduled to [DATE] at [TIME]. Please let us know if this works for you." },
    { id: 'rs2', text: "We've updated your appointment to [DATE] at [TIME]. See you then!" },
    { id: 'rs3', text: "Your new appointment is set for [DATE] at [TIME]. Contact us if you need to make changes." },
    { id: 'custom', text: 'Write custom message...' }
  ],
  cancel: [
    { id: 'cn1', text: "Your appointment has been cancelled. Please contact us if you'd like to reschedule." },
    { id: 'cn2', text: "We've cancelled your appointment as requested. Hope to serve you in the future!" },
    { id: 'cn3', text: "Your appointment has been cancelled. If this was a mistake, please reach out to reschedule." },
    { id: 'custom', text: 'Write custom message...' }
  ]
};

// Helper function to convert 24-hour time to 12-hour format
const formatTimeTo12Hour = (time24) => {
  if (!time24) return '';
  if (time24.includes('-')) {
    const [start, end] = time24.split('-');
    return `${formatSingleTime(start)} - ${formatSingleTime(end)}`;
  }
  return formatSingleTime(time24);
};

const formatSingleTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${hour12}:${minutes?.toString().padStart(2, '0') || '00'} ${period}`;
};

// Time slot options for rescheduling
const TIME_SLOTS = [
  '08:00-09:00', '09:00-10:00', '10:00-11:00', '11:00-12:00',
  '12:00-13:00', '13:00-14:00', '14:00-15:00', '15:00-16:00',
  '16:00-17:00', '17:00-18:00', '18:00-19:00', '19:00-20:00'
];

const JobDetailView = () => {
  const navigate = useNavigate();
  const { appointmentId } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [leadIdent, setLeadIdent] = useState(null);
  
  // Action loading states
  const [actionLoading, setActionLoading] = useState(false);
  
  // Dialog visibility states
  const [showOnMyWayDialog, setShowOnMyWayDialog] = useState(false);
  const [showCancelOnMyWayDialog, setShowCancelOnMyWayDialog] = useState(false);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showServiceTypeEdit, setShowServiceTypeEdit] = useState(false);
  const [convertingEstimate, setConvertingEstimate] = useState(false);

  // MYOS §11 — required-form gate state (added by T1: declaration was missing, causing ReferenceError)
  const [blockedForms, setBlockedForms] = useState(null);
  const [fillFormId, setFillFormId] = useState(null);

  // Form data states
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [editingServiceType, setEditingServiceType] = useState('');
  const [rescheduleData, setRescheduleData] = useState({ 
    date: '', 
    timeSlot: '',
    serviceType: ''
  });

  useEffect(() => {
    loadData();
    loadCurrentUser();
  }, [appointmentId]);

  const loadCurrentUser = () => {
    const userId = localStorage.getItem('user_id');
    const userName = localStorage.getItem('user_name');
    const userRole = localStorage.getItem('user_role') || 'admin';
    setCurrentUser({
      id: userId,
      name: userName || 'Admin',
      role: userRole
    });
  };

  const loadData = async () => {
    try {
      const response = await axios.get(`${API}/appointments/${appointmentId}`);
      setAppointment(response.data);
      setEditingServiceType(response.data.appointment_type || response.data.service_type || '');
      
      if (response.data.customer_id) {
        try {
          const customerResponse = await axios.get(`${API}/customers/${response.data.customer_id}`);
          setCustomer(customerResponse.data);
        } catch (e) {
          console.log('Customer not found, using appointment data');
        }
      }

      // Lead Ecosystem Doctrine §13–19 — resolve RL/EL/WL identity for the
      // header pill (appointment stamp wins, else inherit from customer).
      try {
        const _m = await resolveLeadIdentities({
          appointmentIds: [appointmentId],
          customerIds: [response.data.customer_id].filter(Boolean),
        });
        setLeadIdent(
          (_m.appointments || {})[appointmentId] ||
          (_m.customers || {})[response.data.customer_id] || null
        );
      } catch (e) { /* identity is additive; never blocks the page */ }
    } catch (error) {
      console.error('Error loading appointment:', error);
      toast.error('Failed to load appointment details');
    } finally {
      setLoading(false);
    }
  };

  // Get the final message text based on selection
  const getFinalMessage = (templates) => {
    if (selectedTemplate === 'custom') {
      return customMessage;
    }
    const template = templates.find(t => t.id === selectedTemplate);
    return template?.text || '';
  };

  // Reset dialog state
  const resetDialogState = () => {
    setSelectedTemplate('');
    setCustomMessage('');
  };

  // ============ ACTION HANDLERS ============

  // FIX #2 — convert an approved estimate into an invoice (provider action).
  const convertEstimate = async () => {
    if (!appointment?.pending_estimate_id) return;
    setConvertingEstimate(true);
    try {
      const { data } = await axios.post(`${API}/estimates/${appointment.pending_estimate_id}/convert-to-invoice`);
      toast.success(`Invoice ${data.invoice_number || ''} created from estimate`);
      loadData();
      if (data.invoice_id) navigate(`/invoice/${data.invoice_id}`);
    } catch (error) {
      console.error('Convert estimate failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert estimate');
    } finally {
      setConvertingEstimate(false);
    }
  };

  // ON MY WAY
  const handleOnMyWay = async () => {
    const message = getFinalMessage(MESSAGE_TEMPLATES.onMyWay);
    if (!message || message === 'Write custom message...') {
      toast.error('Please select or write a message');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/appointments/${appointmentId}/on-my-way`, {
        technician_name: currentUser?.name || appointment?.assigned_technician || 'Your technician',
        eta_minutes: 25,
        custom_message: message
      });
      toast.success('On My Way notification sent! 🚗');
      setShowOnMyWayDialog(false);
      resetDialogState();
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setActionLoading(false);
    }
  };

  // CANCEL ON MY WAY
  const handleCancelOnMyWay = async () => {
    const message = getFinalMessage(MESSAGE_TEMPLATES.cancelOnMyWay);
    if (!message || message === 'Write custom message...') {
      toast.error('Please select or write a message');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/appointments/${appointmentId}/cancel-on-my-way`, {
        technician_name: currentUser?.name || appointment?.assigned_technician,
        custom_message: message
      });
      toast.success('On My Way cancelled, customer notified');
      setShowCancelOnMyWayDialog(false);
      resetDialogState();
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to cancel On My Way');
    } finally {
      setActionLoading(false);
    }
  };

  // START APPOINTMENT
  const handleStartAppointment = async () => {
    const message = getFinalMessage(MESSAGE_TEMPLATES.start);
    if (!message || message === 'Write custom message...') {
      toast.error('Please select or write a message');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/appointments/${appointmentId}/start`, {
        technician_name: currentUser?.name || appointment?.assigned_technician || 'Your technician',
        custom_message: message
      });
      toast.success('Appointment started! Customer notified ✅');
      setShowStartDialog(false);
      resetDialogState();
      loadData();
    } catch (error) {
      console.error('Error:', error);
      const d = error.response?.data?.detail;
      if (error.response?.status === 412 && d?.error === 'required_form_missing') {
        setBlockedForms({ stage: 'start', templates: d.missing_templates || [] });
        setShowStartDialog(false);
        toast.error(d.message);
      } else {
        toast.error(typeof d === 'string' ? d : d?.message || 'Failed to start appointment');
      }
    } finally {
      setActionLoading(false);
    }
  };

  // PAUSE APPOINTMENT
  const handlePauseAppointment = async () => {
    const message = getFinalMessage(MESSAGE_TEMPLATES.pause);
    const template = MESSAGE_TEMPLATES.pause.find(t => t.id === selectedTemplate);
    
    if (!message || message === 'Write custom message...') {
      toast.error('Please select or write a message');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/appointments/${appointmentId}/pause`, {
        reason: template?.reason || 'general',
        custom_message: message,
        technician_name: currentUser?.name || appointment?.assigned_technician
      });
      toast.success('Appointment paused, customer notified ⏸️');
      setShowPauseDialog(false);
      resetDialogState();
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to pause appointment');
    } finally {
      setActionLoading(false);
    }
  };

  // COMPLETE APPOINTMENT
  const handleCompleteAppointment = async () => {
    const message = getFinalMessage(MESSAGE_TEMPLATES.complete);
    if (!message || message === 'Write custom message...') {
      toast.error('Please select or write a message');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/appointments/${appointmentId}/complete`, {
        technician_name: currentUser?.name || appointment?.assigned_technician,
        work_performed: appointment?.notes,
        custom_message: message
      });
      // CR-20: also run the ticket money-chain (draft invoice + Ari ticket_completed).
      // Customer was already notified above, so suppress the duplicate completion SMS.
      try {
        const t = await axios.get(`${API}/tickets/appointment/${appointmentId}`);
        if (t.data?.id) {
          await axios.post(`${API}/tickets/${t.data.id}/complete?notify=false`);
        }
      } catch (e) {
        console.warn('Ticket completion sync failed:', e);
      }
      toast.success('Service completed! Invoice drafted & customer notified 🎉');
      setShowCompleteDialog(false);
      resetDialogState();
      loadData();
    } catch (error) {
      console.error('Error:', error);
      const d = error.response?.data?.detail;
      if (error.response?.status === 412 && d?.error === 'required_form_missing') {
        setBlockedForms({ stage: 'complete', templates: d.missing_templates || [] });
        setShowCompleteDialog(false);
        toast.error(d.message);
      } else {
        toast.error(typeof d === 'string' ? d : d?.message || 'Failed to complete appointment');
      }
    } finally {
      setActionLoading(false);
    }
  };

  // RESCHEDULE APPOINTMENT
  const handleReschedule = async (notify = false) => {
    if (!rescheduleData.date || !rescheduleData.timeSlot) {
      toast.error('Please select both date and time slot');
      return;
    }

    let message = '';
    if (notify) {
      message = getFinalMessage(MESSAGE_TEMPLATES.reschedule);
      if (!message || message === 'Write custom message...') {
        toast.error('Please select or write a message');
        return;
      }
      // Replace placeholders
      message = message
        .replace('[DATE]', rescheduleData.date)
        .replace('[TIME]', formatTimeTo12Hour(rescheduleData.timeSlot));
    }

    setActionLoading(true);
    try {
      // Update appointment
      await axios.put(`${API}/appointments/${appointmentId}`, {
        appointment_date: rescheduleData.date,
        time_slot: rescheduleData.timeSlot,
        appointment_type: rescheduleData.serviceType || appointment?.appointment_type,
        service_type: rescheduleData.serviceType || appointment?.service_type
      });

      // Send notification if requested
      if (notify && message) {
        await axios.post(`${API}/appointments/${appointmentId}/notify-reschedule`, {
          new_date: rescheduleData.date,
          new_time: formatTimeTo12Hour(rescheduleData.timeSlot),
          custom_message: message
        });
        toast.success('Appointment rescheduled and customer notified! 📅');
      } else {
        toast.success('Appointment rescheduled (customer not notified)');
      }

      setShowRescheduleDialog(false);
      resetDialogState();
      setRescheduleData({ date: '', timeSlot: '', serviceType: '' });
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to reschedule');
    } finally {
      setActionLoading(false);
    }
  };

  // CANCEL APPOINTMENT
  const handleCancelAppointment = async () => {
    const message = getFinalMessage(MESSAGE_TEMPLATES.cancel);
    if (!message || message === 'Write custom message...') {
      toast.error('Please select or write a message');
      return;
    }

    setActionLoading(true);
    try {
      await axios.delete(`${API}/appointments/${appointmentId}/cancel`, {
        params: { reason: message }
      });
      toast.success('Appointment cancelled, customer notified');
      setShowCancelDialog(false);
      resetDialogState();
      navigate(-1);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to cancel appointment');
    } finally {
      setActionLoading(false);
    }
  };

  // GET DIRECTIONS
  const handleGetDirections = () => {
    const address = appointment?.address || customer?.address;
    if (!address) {
      toast.error('No address available');
      return;
    }
    
    const encodedAddress = encodeURIComponent(address);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    let mapsUrl;
    if (isIOS) {
      mapsUrl = `maps://maps.apple.com/?daddr=${encodedAddress}`;
    } else if (isAndroid) {
      mapsUrl = `geo:0,0?q=${encodedAddress}`;
    } else {
      mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
    }
    
    window.open(mapsUrl, '_blank');
  };

  // MESSAGE CUSTOMER
  const handleMessageCustomer = () => {
    const phone = customer?.phone || appointment?.customer_phone;
    if (!phone) {
      toast.error('Customer phone number not available');
      return;
    }
    window.location.href = `sms:${phone}`;
  };

  // UPDATE SERVICE TYPE
  const handleUpdateServiceType = async () => {
    if (!editingServiceType.trim()) {
      toast.error('Please enter a service type');
      return;
    }
    
    try {
      await axios.put(`${API}/appointments/${appointmentId}`, {
        appointment_type: editingServiceType,
        service_type: editingServiceType
      });
      toast.success('Service type updated!');
      setShowServiceTypeEdit(false);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update service type');
    }
  };

  // Determine status phase for UI
  const getStatusPhase = () => {
    const status = appointment?.status?.toLowerCase();
    if (status === 'scheduled' || status === 'sch') return 'scheduled';
    if (status === 'ir' || status === 'in_route' || status === 'on_the_way' || status === 'on_my_way' || status === 'en_route') return 'on_way';
    if (status === 'ip' || status === 'in_progress') return 'in_progress';
    if (status === 'paused') return 'paused';
    if (status === 'completed' || status === 'done') return 'completed';
    if (status === 'cancelled') return 'cancelled';
    return 'scheduled';
  };

  // ============ DIALOG COMPONENT ============
  const MessageTemplateDialog = ({ 
    show, 
    onClose, 
    title, 
    icon: Icon, 
    iconColor,
    templates, 
    onSubmit, 
    submitText,
    submitColor = 'bg-blue-500 hover:bg-blue-600'
  }) => {
    if (!show) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg flex items-center gap-2">
              <Icon className={`w-5 h-5 ${iconColor}`} />
              {title}
            </h3>
            <button onClick={() => { onClose(); resetDialogState(); }}>
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Select a message:</Label>
              <RadioGroup value={selectedTemplate} onValueChange={setSelectedTemplate}>
                {templates.map((template) => (
                  <div key={template.id} className="flex items-start space-x-3 p-3 rounded-xl hover:bg-gray-50 border border-gray-100">
                    <RadioGroupItem value={template.id} id={template.id} className="mt-1" />
                    <Label htmlFor={template.id} className="text-sm cursor-pointer flex-1">
                      {template.text}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            
            {selectedTemplate === 'custom' && (
              <div>
                <Label className="text-sm font-medium mb-2 block">Your message:</Label>
                <Textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Type your custom message..."
                  rows={3}
                  className="w-full"
                />
              </div>
            )}
            
            <div className="flex gap-2 pt-2">
              <Button 
                onClick={onSubmit} 
                disabled={actionLoading || (!selectedTemplate || (selectedTemplate === 'custom' && !customMessage.trim()))}
                className={`flex-1 ${submitColor}`}
              >
                {actionLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> {submitText}</>
                )}
              </Button>
              <Button variant="outline" onClick={() => { onClose(); resetDialogState(); }} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============ RENDER ============
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div>Appointment not found</div>
      </div>
    );
  }

  const formattedTimeSlot = formatTimeTo12Hour(appointment.time_slot);
  const statusPhase = getStatusPhase();

  return (
    <div className="bg-gray-50 min-h-screen pb-6">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <StandardBackButton />
          <h1 className="text-xl font-semibold text-gray-900">
            {customer?.name || appointment.customer_name || 'Job Details'}
            {leadIdent && <LeadIdentityBadge leadType={leadIdent.lead_type} leadSource={leadIdent.lead_source} className="ml-2 align-middle" />}
          </h1>
        </div>
        <button onClick={handleMessageCustomer} data-testid="message-customer-header-btn">
          <MessageSquare className="w-6 h-6 text-blue-500" />
        </button>
      </div>

      {/* Status Badge */}
      <div className="px-4 pt-3">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          statusPhase === 'completed' ? 'bg-green-100 text-green-800' :
          statusPhase === 'in_progress' ? 'bg-blue-100 text-blue-800' :
          statusPhase === 'on_way' ? 'bg-orange-100 text-orange-800' :
          statusPhase === 'paused' ? 'bg-yellow-100 text-yellow-800' :
          statusPhase === 'cancelled' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {appointment.status?.toUpperCase() || 'SCHEDULED'}
        </span>
      </div>

      {/* FIX #2 — Estimate status banner (Diagnosis → Estimate → Approval) */}
      {appointment.estimate_status && (
        <div className="px-4 pt-3">
          <div
            className={`rounded-xl p-3 border ${
              appointment.estimate_status === 'approved' ? 'bg-emerald-50 border-emerald-200'
              : appointment.estimate_status === 'declined' ? 'bg-red-50 border-red-200'
              : appointment.estimate_status === 'converted' ? 'bg-blue-50 border-blue-200'
              : 'bg-violet-50 border-violet-200'
            }`}
            data-testid="estimate-status-banner"
          >
            <div className="flex items-center gap-2">
              <FileCheck className={`w-4 h-4 ${
                appointment.estimate_status === 'approved' ? 'text-emerald-600'
                : appointment.estimate_status === 'declined' ? 'text-red-600'
                : appointment.estimate_status === 'converted' ? 'text-blue-600'
                : 'text-violet-600'
              }`} />
              <p className="text-sm font-semibold text-gray-800">
                {appointment.estimate_status === 'approved' && 'Estimate approved by customer'}
                {appointment.estimate_status === 'declined' && 'Estimate declined by customer'}
                {appointment.estimate_status === 'converted' && 'Estimate converted to invoice'}
                {['sent', 'viewed', 'pending'].includes(appointment.estimate_status) && 'Estimate sent — awaiting customer approval'}
              </p>
            </div>
            {appointment.estimate_status === 'approved' && (
              <button
                onClick={convertEstimate}
                disabled={convertingEstimate}
                className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                data-testid="convert-estimate-btn"
              >
                {convertingEstimate ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Convert to Invoice
              </button>
            )}
            {appointment.estimate_status === 'converted' && appointment.invoice_id && (
              <button
                onClick={() => navigate(`/invoice/${appointment.invoice_id}`)}
                className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-blue-700 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                data-testid="view-invoice-btn"
              >
                <FileText className="w-4 h-4" /> View Invoice
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Action Buttons */}
      <div className="p-4 space-y-3">
        <WorkspaceJobPanel appointmentId={appointmentId} customerId={appointment?.customer_id} />
        {/* MYOS §11 — required-form gate: let the user complete the wired form right here */}
        {blockedForms && (
          <div data-testid="required-form-banner" className="bg-amber-50 border border-amber-300 rounded-xl p-4">
            <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
              <AlertTriangle className="w-4 h-4" />
              Required form before you can {blockedForms.stage === 'complete' ? 'complete' : 'start'} this job
            </div>
            {blockedForms.templates.map((t) => (
              <button
                key={t.id}
                data-testid={`fill-required-form-${t.id}`}
                onClick={() => setFillFormId(t.id)}
                className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
              >
                <FileText className="w-4 h-4" /> Fill out “{t.name}”
              </button>
            ))}
          </div>
        )}
        {fillFormId && (
          <MyosFormFillModal
            templateId={fillFormId}
            appointmentId={appointmentId}
            onClose={() => setFillFormId(null)}
            onDone={() => {
              setBlockedForms(null);
              toast.success('Form submitted — you can proceed now.');
            }}
          />
        )}
        {statusPhase === 'scheduled' && (
          <button
            onClick={() => setShowOnMyWayDialog(true)}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-xl py-4 text-lg font-semibold shadow-sm transition-colors flex items-center justify-center gap-2"
            data-testid="on-my-way-btn"
          >
            <Navigation className="w-5 h-5" />
            On My Way
          </button>
        )}

        {statusPhase === 'on_way' && (
          <>
            <button
              onClick={() => setShowStartDialog(true)}
              className="w-full bg-green-500 hover:bg-green-600 text-white rounded-xl py-4 text-lg font-semibold shadow-sm transition-colors flex items-center justify-center gap-2"
              data-testid="start-appointment-btn"
            >
              <Play className="w-5 h-5" />
              Start Appointment
            </button>
            <button
              onClick={() => setShowCancelOnMyWayDialog(true)}
              className="w-full bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-xl py-3 text-base font-medium transition-colors flex items-center justify-center gap-2 border border-yellow-300"
              data-testid="cancel-on-my-way-btn"
            >
              <XCircle className="w-4 h-4" />
              Cancel On My Way (Sent by mistake)
            </button>
          </>
        )}

        {statusPhase === 'in_progress' && (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setShowPauseDialog(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl py-4 text-lg font-semibold transition-colors flex items-center justify-center gap-2"
              data-testid="pause-btn"
            >
              <Pause className="w-5 h-5" />
              Pause
            </button>
            <button
              onClick={() => setShowCompleteDialog(true)}
              className="bg-green-500 hover:bg-green-600 text-white rounded-xl py-4 text-lg font-semibold transition-colors flex items-center justify-center gap-2"
              data-testid="done-btn"
            >
              <CheckCircle className="w-5 h-5" />
              Done
            </button>
          </div>
        )}

        {statusPhase === 'paused' && (
          <div className="space-y-3">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-yellow-800 font-medium mb-1">
                <AlertTriangle className="w-5 h-5" />
                Appointment Paused
              </div>
              <p className="text-sm text-yellow-700">
                Reason: {appointment.pause_reason || 'Not specified'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setShowStartDialog(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl py-4 text-lg font-semibold transition-colors flex items-center justify-center gap-2"
                data-testid="resume-btn"
              >
                <Play className="w-5 h-5" />
                Resume
              </button>
              <button
                onClick={() => setShowCompleteDialog(true)}
                className="bg-green-500 hover:bg-green-600 text-white rounded-xl py-4 text-lg font-semibold transition-colors flex items-center justify-center gap-2"
                data-testid="done-btn"
              >
                <CheckCircle className="w-5 h-5" />
                Done
              </button>
            </div>
          </div>
        )}

        {statusPhase === 'completed' && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-2 text-green-800 font-medium">
              <CheckCircle className="w-5 h-5" />
              Service Completed
            </div>
            {appointment.completed_at && (
              <p className="text-sm text-green-700 mt-1">
                Completed: {new Date(appointment.completed_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {statusPhase === 'cancelled' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-2 text-red-800 font-medium">
              <XCircle className="w-5 h-5" />
              Appointment Cancelled
            </div>
          </div>
        )}
      </div>

      {/* Service Type - Editable */}
      <div className="mx-4 mb-4 bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Label className="text-xs text-gray-500">SERVICE TYPE</Label>
            {showServiceTypeEdit ? (
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={editingServiceType}
                  onChange={(e) => setEditingServiceType(e.target.value)}
                  placeholder="Enter service type"
                  className="flex-1"
                  data-testid="service-type-input"
                />
                <Button size="sm" onClick={handleUpdateServiceType}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => setShowServiceTypeEdit(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <p 
                className="font-medium text-gray-900 cursor-pointer hover:text-blue-600 flex items-center gap-2"
                onClick={() => setShowServiceTypeEdit(true)}
                data-testid="service-type-display"
              >
                {appointment.appointment_type || appointment.service_type || 'Click to set'} 
                <Edit3 className="w-3 h-3 text-blue-500" />
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Unified Job Detail Component */}
      <div className="px-4">
        <UnifiedJobDetail
          appointmentId={appointmentId}
          currentUser={currentUser}
          viewMode="page"
          appointment={appointment}
          customer={customer}
        />
      </div>

      {/* Schedule Section */}
      <div className="mx-4 mt-4 bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4">
          <h3 className="text-base font-semibold text-gray-700 mb-4">Schedule</h3>

          <div className="space-y-3 mb-4">
            <div className="text-center py-3 bg-gray-50 rounded-xl border border-gray-200">
              <div className="text-gray-700 text-sm mb-1">{appointment.appointment_date}</div>
              <div className="text-gray-700 font-medium">{formattedTimeSlot}</div>
              {appointment.assigned_technician && (
                <div className="text-sm text-gray-600 mt-2">Assigned: {appointment.assigned_technician}</div>
              )}
            </div>

            {statusPhase !== 'completed' && statusPhase !== 'cancelled' && (
              <>
                <button 
                  onClick={() => {
                    setRescheduleData({ 
                      date: appointment.appointment_date, 
                      timeSlot: appointment.time_slot,
                      serviceType: appointment.appointment_type || appointment.service_type || ''
                    });
                    setShowRescheduleDialog(true);
                  }}
                  className="w-full text-blue-500 py-2 text-base hover:bg-blue-50 rounded-xl transition-colors border border-blue-200 flex items-center justify-center gap-2"
                  data-testid="reschedule-btn"
                >
                  <Calendar className="w-4 h-4" />
                  Reschedule
                </button>
                <button 
                  onClick={() => navigate(`/schedule?reassign=${appointmentId}`)}
                  className="w-full text-blue-500 py-2 text-base hover:bg-blue-50 rounded-xl transition-colors border border-blue-200"
                  data-testid="reassign-btn"
                >
                  Reassign
                </button>
                <button 
                  onClick={() => setShowCancelDialog(true)}
                  className="w-full text-red-500 py-2 text-base hover:bg-red-50 rounded-xl transition-colors border border-red-200"
                  data-testid="cancel-appointment-btn"
                >
                  Cancel Appointment
                </button>
              </>
            )}
          </div>

          {/* Street View Property Preview */}
          {appointment.address && (
            <div className="mb-3 relative overflow-hidden rounded-xl" data-testid="street-view-container">
              <img
                src={`https://maps.googleapis.com/maps/api/streetview?size=600x300&location=${encodeURIComponent(appointment.address)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&fov=90&pitch=10`}
                alt="Property view"
                className="w-full h-48 object-cover rounded-xl"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                data-testid="street-view-image"
              />
              <div className="w-full h-48 bg-gray-200 rounded-xl items-center justify-center hidden">
                <MapPin className="w-8 h-8 text-gray-400" />
              </div>
              <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[10px] px-2 py-1 rounded-md flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Street View
              </div>
            </div>
          )}
          {!appointment.address && (
            <div className="mb-3 h-48 bg-gray-200 rounded-xl flex items-center justify-center">
              <MapPin className="w-8 h-8 text-gray-400" />
            </div>
          )}

          {/* Address & Directions */}
          <div className="mb-3">
            <p className="text-gray-700 text-base mb-2">{appointment.address}</p>
            <button 
              onClick={handleGetDirections}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-xl py-3 text-base font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="get-directions-btn"
            >
              <Navigation className="w-5 h-5" />
              Get Directions
            </button>
          </div>

          {/* Date/Time Footer */}
          <div className="flex justify-between text-sm text-gray-700 pt-3 border-t border-gray-200">
            <div>
              <div className="text-gray-500 text-xs mb-1">DATE</div>
              <div className="font-medium">{appointment.appointment_date}</div>
            </div>
            <div className="text-right">
              <div className="text-gray-500 text-xs mb-1">TIME</div>
              <div className="font-medium">{formattedTimeSlot}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ DIALOGS ============ */}

      {/* On My Way Dialog */}
      <MessageTemplateDialog
        show={showOnMyWayDialog}
        onClose={() => setShowOnMyWayDialog(false)}
        title="On My Way"
        icon={Navigation}
        iconColor="text-blue-600"
        templates={MESSAGE_TEMPLATES.onMyWay}
        onSubmit={handleOnMyWay}
        submitText="Send & Notify"
        submitColor="bg-blue-500 hover:bg-blue-600"
      />

      {/* Cancel On My Way Dialog */}
      <MessageTemplateDialog
        show={showCancelOnMyWayDialog}
        onClose={() => setShowCancelOnMyWayDialog(false)}
        title="Cancel On My Way"
        icon={XCircle}
        iconColor="text-yellow-600"
        templates={MESSAGE_TEMPLATES.cancelOnMyWay}
        onSubmit={handleCancelOnMyWay}
        submitText="Send Correction"
        submitColor="bg-yellow-500 hover:bg-yellow-600"
      />

      {/* Start Appointment Dialog */}
      <MessageTemplateDialog
        show={showStartDialog}
        onClose={() => setShowStartDialog(false)}
        title="Start Appointment"
        icon={Play}
        iconColor="text-green-600"
        templates={MESSAGE_TEMPLATES.start}
        onSubmit={handleStartAppointment}
        submitText="Start & Notify"
        submitColor="bg-green-500 hover:bg-green-600"
      />

      {/* Pause Dialog */}
      <MessageTemplateDialog
        show={showPauseDialog}
        onClose={() => setShowPauseDialog(false)}
        title="Pause Appointment"
        icon={Pause}
        iconColor="text-yellow-600"
        templates={MESSAGE_TEMPLATES.pause}
        onSubmit={handlePauseAppointment}
        submitText="Pause & Notify"
        submitColor="bg-yellow-500 hover:bg-yellow-600"
      />

      {/* Complete Dialog */}
      <MessageTemplateDialog
        show={showCompleteDialog}
        onClose={() => setShowCompleteDialog(false)}
        title="Complete Service"
        icon={CheckCircle}
        iconColor="text-green-600"
        templates={MESSAGE_TEMPLATES.complete}
        onSubmit={handleCompleteAppointment}
        submitText="Complete & Notify"
        submitColor="bg-green-500 hover:bg-green-600"
      />

      {/* Cancel Appointment Dialog */}
      <MessageTemplateDialog
        show={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        title="Cancel Appointment"
        icon={XCircle}
        iconColor="text-red-600"
        templates={MESSAGE_TEMPLATES.cancel}
        onSubmit={handleCancelAppointment}
        submitText="Cancel & Notify"
        submitColor="bg-red-500 hover:bg-red-600"
      />

      {/* Reschedule Dialog - Custom (not using template dialog) */}
      {showRescheduleDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Reschedule Appointment
              </h3>
              <button onClick={() => { setShowRescheduleDialog(false); resetDialogState(); }}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Date */}
              <div>
                <Label className="text-sm font-medium mb-2 block">New Date</Label>
                <Input
                  type="date"
                  value={rescheduleData.date}
                  onChange={(e) => setRescheduleData({...rescheduleData, date: e.target.value})}
                  className="w-full"
                  data-testid="reschedule-date-input"
                />
              </div>

              {/* Time Slot */}
              <div>
                <Label className="text-sm font-medium mb-2 block">New Time Slot</Label>
                <Select 
                  value={rescheduleData.timeSlot} 
                  onValueChange={(val) => setRescheduleData({...rescheduleData, timeSlot: val})}
                >
                  <SelectTrigger data-testid="reschedule-time-select">
                    <SelectValue placeholder="Select time slot..." />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {formatTimeTo12Hour(slot)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Service Type */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Service Type</Label>
                <Input
                  value={rescheduleData.serviceType}
                  onChange={(e) => setRescheduleData({...rescheduleData, serviceType: e.target.value})}
                  placeholder="Enter service type..."
                  className="w-full"
                  data-testid="reschedule-service-input"
                />
              </div>

              {/* Notification Message (optional) */}
              <div className="border-t pt-4 mt-4">
                <Label className="text-sm font-medium mb-2 block">Notification Message (if notifying customer)</Label>
                <RadioGroup value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  {MESSAGE_TEMPLATES.reschedule.map((template) => (
                    <div key={template.id} className="flex items-start space-x-3 p-2 rounded-xl hover:bg-gray-50">
                      <RadioGroupItem value={template.id} id={`rs-${template.id}`} className="mt-1" />
                      <Label htmlFor={`rs-${template.id}`} className="text-sm cursor-pointer flex-1">
                        {template.text}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                
                {selectedTemplate === 'custom' && (
                  <Textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Type your custom message..."
                    rows={2}
                    className="w-full mt-2"
                  />
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-2 pt-2">
                <Button 
                  onClick={() => handleReschedule(true)}
                  disabled={actionLoading || !rescheduleData.date || !rescheduleData.timeSlot || !selectedTemplate || (selectedTemplate === 'custom' && !customMessage.trim())}
                  className="w-full bg-blue-500 hover:bg-blue-600"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                  Reschedule & Notify Customer
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => handleReschedule(false)}
                  disabled={actionLoading || !rescheduleData.date || !rescheduleData.timeSlot}
                  className="w-full"
                >
                  Reschedule Only (No Notification)
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => { setShowRescheduleDialog(false); resetDialogState(); }}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Menu */}
      <FloatingActionMenu 
        appointmentId={appointmentId} 
        customerId={appointment?.customer_id} 
      />
    </div>
  );
};

export default JobDetailView;
